import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { offlineGatingMessages } from './offline-gating-messages';

/**
 * The string-prop counterpart to `OfflineGatedTooltip`.
 *
 * Controls that render their own tooltip from a `string` prop (`tooltip`,
 * `disabledTooltip`, `title`) can't be wrapped by `OfflineGatedTooltip` — there
 * is no trigger to wrap, only a label to swap. Those call sites each repeated
 * the same two lines to get the formatted copy, and the sidebar footer
 * additionally grew a local `gatedTooltip(label)` helper to pick between the
 * offline copy and the control's own label. This is the shared home for both.
 *
 * **Adoption is partial.** Every site whose tooltip is a *ternary between the
 * offline copy and a label* now reads from here — the sidebar footer, referral
 * and trial-countdown controls, the Ask Heidi floating composer's protocols and
 * send gates, the Chat composer's protocols menu, and the clinician upgrade CTA.
 *
 * Four sites still inline the two lines, and all four want `offlineTooltip`
 * rather than `gatedTooltip` — they have no label to choose against, so reading
 * from here removes a duplicated `formatMessage` but no branching.
 * `useStaticItems` and `v2-artifact-tabs` pass the string as an unconditional
 * `disabledTooltip` on a row whose only disabled reason is the gate;
 * `use-session-list-table` and `session-list-header` format it and hand it down
 * as a prop, leaving the ternary to a consumer that receives both halves.
 *
 * Three sites cannot adopt `gatedTooltip` at all, and should not be filed as
 * debt. `evidence-note-overlay-input-row` and `session-list-toolbar-actions`
 * take `isOfflineGated` as a **prop** rather than reading the gate, so this hook
 * would be a second and disagreeing source of it. `sessionListHeader` is the
 * offline-or-label wrapper shape, where the label side is a `FormattedMessage`
 * element rather than a string — widening the label type to `ReactNode` to reach
 * it would give up the narrowing documented below. `SideNavFooter`'s menu-item
 * tooltip is tracked on its own ticket for the `DropdownMenuItem` shape.
 *
 * Prefer this hook in new code, and migrate a site when you are next in it.
 *
 * `gatedTooltip` returns the offline copy while gated and `label` otherwise, so
 * `gatedTooltip(undefined)` is the right call for a control whose tooltip should
 * only exist while gated. Its label type is deliberately narrow: a tooltip label
 * is a string, or absent on a control that should only have one while gated.
 * Anything else is a call site doing something this hook is not for.
 *
 * It keeps a stable identity across renders, so an adopter may put it in a
 * dependency array or hand it to a memoised child without silently losing the
 * memoisation. The returned object is deliberately NOT memoised — every call
 * site destructures it where it is called, so nothing holds the object itself.
 *
 * `isOfflineGated` is a re-export of `useOfflineGating().shouldGateControls`,
 * not a tooltip-specific gate. A control that needs the same boolean for
 * `disabled`, a click guard or a style is reading the same source either way;
 * taking it from here is what keeps a site from calling both hooks to get one
 * gate and one string.
 *
 * `offlineTooltip` is exposed for the call sites that need the raw string inside
 * a wider branch — a three-way `disabledTooltip` where offline is only one of
 * the disabled reasons, for instance.
 *
 * This is deliberately NOT a way to gate a wrapped control: if the control can
 * host a tooltip trigger, use `OfflineGatedTooltip`, which also keeps the
 * tooltip reachable on a `pointer-events-none` disabled button.
 */
export const useOfflineGatedTooltip = () => {
  const intl = useIntl();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const offlineTooltip = intl.formatMessage(offlineGatingMessages.notAvailableOffline);

  const gatedTooltip = useCallback(
    <T extends string | undefined>(label: T) => (isOfflineGated ? offlineTooltip : label),
    [isOfflineGated, offlineTooltip]
  );

  return { isOfflineGated, offlineTooltip, gatedTooltip };
};
